<template>
    <div class="privacy-settings">
        <div class="animated fadeIn">
            <div v-if="loading" class="loading-container">
                <atom-spinner
                    :animation-duration="1000"
                    :size="60"
                    color="#ff1d5e"
                />
            </div>
            <div v-else class="animated fadeIn">
                <component-header
                    title="Privacy Setting"
                    :isLoading="loading"
                    :hasReload="true"
                    @search-query="onSearchQuery"
                    @create-press="createPrivacySetting"
                    @reload-data="fetchData"
                    createButtonText="Create Privacy Setting"
                />
                <b-card class="card-wrap" border-variant="info">
                    <b-row class="justify-content-center">
                        <b-col>
                            <div class="table-wrapper">
                                <b-table
                                    class="text-center tableRadius privacy-table"
                                    :items="tableData"
                                    :fields="fields"
                                    :bordered="bordered"
                                    :filter="filter"
                                    responsive
                                    thead-class="header"
                                    :busy="loading"
                                    :sort-by.sync="sortBy"
                                    :sort-desc.sync="sortDesc"
                                    show-empty
                                >
                                    <template #empty>
                                        <h4 class="text-center mb-0 py-4">
                                            No Data Available
                                        </h4>
                                    </template>
                                    <template #emptyfiltered>
                                        <h4 class="text-center mb-0 py-4">
                                            No Data Available
                                        </h4>
                                    </template>
                                    <template #cell(createdAt)="data">
                                        {{ formatDate(data.item.createdAt) }}
                                    </template>
                                    <template #cell(updatedAt)="data">
                                        {{ formatDate(data.item.updatedAt) }}
                                    </template>
                                    <template #cell(status)="data">
                                        <div
                                            class="toggle-container"
                                            :class="{
                                                'toggle-on': data.item.isOn,
                                                'toggle-disabled':
                                                    data.item._toggling,
                                            }"
                                            @click="toggle(data.item)"
                                        >
                                            <div class="toggle-button"></div>
                                        </div>
                                    </template>
                                    <template #cell(action)="data">
                                        <div class="inline_btnTable">
                                            <b-button
                                                size="sm"
                                                class="button-circle mr-2 edit-btn-orange"
                                                variant="primary"
                                                @click="editItem(data.item)"
                                            >
                                                <i class="fa fa-pencil"></i>
                                            </b-button>
                                            <b-button
                                                size="sm"
                                                class="button-circle"
                                                variant="danger"
                                                @click="deleteItem(data.item)"
                                            >
                                                <i class="fa fa-trash"></i>
                                            </b-button>
                                        </div>
                                    </template>
                                </b-table>
                            </div>
                        </b-col>
                    </b-row>
                </b-card>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import ComponentHeader from "@/components/componentNewHeader";
import { AtomSpinner } from "epic-spinners";
import Swal from "sweetalert2";

export default {
    name: "PrivacySetting",
    components: {
        ComponentHeader,
        AtomSpinner,
    },
    data() {
        return {
            loading: true,
            filter: "",
            sortBy: "",
            sortDesc: true,
            bordered: true,
            tableData: [],
            fields: [
                { key: "title", label: "TITLE", sortable: true },
                { key: "content", label: "TYPE", sortable: true },
                { key: "scope", label: "SCOPE", sortable: true },
                { key: "createdAt", label: "CREATED ON" },
                { key: "updatedAt", label: "UPDATED ON" },
                { key: "status", label: "STATUS" },
                { key: "action", label: "ACTION" },
            ],
        };
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        onSearchQuery(value) {
            this.filter = value;
        },
        createPrivacySetting() {
            this.$router.push({ name: "Create Privacy Setting" });
        },
        async toggle(item) {
            if (item._toggling) return;
            const newIsOn = !item.isOn;
            const newStatus = newIsOn ? "Active" : "Inactive";
            item._toggling = true;

            try {
                await this.$store.dispatch(
                    "privacySetting/viewPrivacySettingAction",
                    {
                        tenant: item.tenant || this.$currentUserData._id,
                        content: item.content,
                        slug: item.scope,
                    },
                );
                const fullData =
                    this.$store.getters[
                        "privacySetting/GetEditPrivacySettingData"
                    ];
                const sections = (fullData?.sections || []).map((s) => ({
                    subtitle: s.subtitle || "",
                    description: s.description || "",
                }));
                const slug = (item.title || "")
                    .toLowerCase()
                    .trim()
                    .replace(/\s+/g, "-")
                    .replace(/[^\w\-]+/g, "")
                    .replace(/\-\-+/g, "-");

                await this.$store.dispatch(
                    "privacySetting/editPrivacySettingAction",
                    {
                        id: item._id,
                        data: {
                            tenant: item.tenant || this.$currentUserData._id,
                            sections: sections.length
                                ? sections
                                : [{ subtitle: "", description: "" }],
                            title: item.title,
                            slug,
                            content: item.content,
                            scope: item.scope,
                            status: newStatus,
                        },
                    },
                );
                item.isOn = newIsOn;
                this.$toasted.success("Status updated successfully");
            } catch (error) {
                console.error("Error updating status:", error);
                this.$toasted.error("Failed to update status");
            } finally {
                item._toggling = false;
            }
        },
        formatDate(date) {
            return date ? moment(date).format("DD/MM/YYYY HH:mm") : "";
        },
        async fetchData() {
            this.loading = true;
            try {
                const currentUserId = this.$currentUserData._id;
                const response = await this.$store.dispatch(
                    "privacySetting/listPrivacySettingAction",
                    currentUserId,
                );
                this.tableData = (response.data || []).map((item) => ({
                    ...item,
                    isOn:
                        item.status === "Active" || item.isOn === true || false,
                }));
            } catch (error) {
                console.error("Error fetching privacy settings:", error);
            } finally {
                this.loading = false;
            }
        },
        async deleteItem(item) {
            const confirmed = await Swal.fire({
                title: "Are you sure?",
                text: "You can't revert this action!!",
                type: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "No, keep it",
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
            });

            if (!confirmed.value) return;

            try {
                await this.$store.dispatch(
                    "privacySetting/deletePrivacySettingAction",
                    item._id,
                );
                this.$toasted.success("Privacy Deleted Successfully");
                this.fetchData();
            } catch (error) {
                console.error("Error deleting privacy setting:", error);
            }
        },
        editItem(item) {
            this.$router.push({
                name: "Edit Privacy Setting",
                params: { data: item },
            });
        },
    },
};
</script>

<style scoped>
.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.privacy-settings {
    background-color: #f9f9f9;
    min-height: 100vh;
}

.table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.tableRadius {
    border-radius: 12px;
    background-color: #fff;
}

.edit-btn-orange {
    background: #fe7b08;
    border: #fe7b08;
}

.edit-btn-orange:hover {
    background: #e66a00;
    border-color: #e66a00;
}

.toggle-container {
    display: inline-block;
    width: 50px;
    height: 25px;
    background-color: #ccc;
    border-radius: 15px;
    position: relative;
    cursor: pointer;
    transition: background-color 0.3s;
}

.toggle-container.toggle-on {
    background-color: #4caf50;
}

.toggle-container.toggle-disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
}

.toggle-button {
    width: 23px;
    height: 23px;
    background-color: white;
    border-radius: 50%;
    position: absolute;
    top: 1px;
    left: 1px;
    transition: left 0.3s;
}

.toggle-container.toggle-on .toggle-button {
    left: 26px;
}

@media (max-width: 767.98px) {
    .privacy-settings {
        padding: 10px;
    }

    .tableRadius {
        border-radius: 8px;
    }

    .table-wrapper {
        margin: 0 -15px;
    }

    .tableRadius .b-table-stacked {
        display: block;
        width: 100%;
    }

    .toggle-container {
        width: 40px;
        height: 20px;
    }

    .toggle-button {
        width: 18px;
        height: 18px;
    }

    .toggle-container.toggle-on .toggle-button {
        left: 20px;
    }
}

@media (max-width: 767px) {
    .tableRadius td {
        white-space: normal;
        word-break: break-word;
    }
}
</style>
